/**
 * Tags Panel Component - Left Panel
 * Displays tabbed interface for tag management: + Tag, Tag List, and Tag Editor
 */

import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { TagCandidate, TAG_COLORS } from '../../types';
import TagEditor from './TagEditor';
import ConfidenceScore from '../common/ConfidenceScore';
import CompetencySearch from './CompetencySearch';

interface TagsPanelProps {
  tagCandidates: TagCandidate[];
  focusedTagIndex: number | null;
  lessonId: string;
  onFocusedTagChange: (index: number | null) => void;
  onTagUpdate: (updatedTag: TagCandidate, tagIndex: number) => void;
  onAddTagCandidates: (newTagCandidates: TagCandidate[]) => void;
}

export default function TagsPanel({
  tagCandidates,
  focusedTagIndex,
  lessonId,
  onFocusedTagChange,
  onTagUpdate,
  onAddTagCandidates,
}: TagsPanelProps) {
  const [selectedTab, setSelectedTab] = useState(2); // Default to Tag Editor tab

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleTagCardClick = (originalIndex: number) => {
    onFocusedTagChange(originalIndex);
    // TODO: Revisit - automatically switching to Tag Editor tab
    // setSelectedTab(2); // Switch to Tag Editor tab when a tag is selected
  };

  // Sort tag candidates alphabetically by description, but preserve original indices
  const sortedTagsWithIndices = useMemo(() => {
    return tagCandidates
      .map((tag, originalIndex) => ({ tag, originalIndex }))
      .sort((a, b) =>
        a.tag.competency.description.localeCompare(b.tag.competency.description, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      );
  }, [tagCandidates]);

  // Get the currently focused tag
  const focusedTag = focusedTagIndex !== null ? tagCandidates[focusedTagIndex] : null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Tags
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="tag tabs">
          <Tab label="+ Tag" />
          <Tab label="Tag List" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Tag Editor
                {focusedTagIndex !== null && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: TAG_COLORS.FOCUSED_TAG_BORDER,
                      ml: 0.7
                    }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* + Tag Tab */}
        {selectedTab === 0 && (
          <CompetencySearch onAddTagCandidates={onAddTagCandidates} />
        )}

        {/* Tag List Tab */}
        {selectedTab === 1 && (
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              All Tag Candidates ({tagCandidates.length})
            </Typography>

            {tagCandidates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  No tag candidates available
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {sortedTagsWithIndices.map(({ tag, originalIndex }) => (
                  <Card
                    key={`${tag.content_id}-${tag.competency.spine_node_id}`}
                    sx={{
                      cursor: 'pointer',
                      borderLeft: 4,
                      borderLeftColor: focusedTagIndex === originalIndex ? 'warning.main' : 'transparent',
                      bgcolor: tag.competency.selected ? 'rgba(99, 102, 241, 0.15)' : 'background.paper',
                      border: 1,
                      borderColor: tag.competency.selected ? TAG_COLORS.ALIGNED_BORDER : 'divider',
                      '&:hover': {
                        bgcolor: tag.competency.selected
                          ? 'rgba(99, 102, 241, 0.25)'
                          : 'action.hover',
                      },
                    }}
                    onClick={() => handleTagCardClick(originalIndex)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Competency Info */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', mb: 0.5 }}>
                          {tag.competency.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {tag.competency.spine_node_id}
                        </Typography>
                      </Box>

                      {/* Confidence Score */}
                      <Box sx={{ mb: 1 }}>
                        <ConfidenceScore
                          score={tag.competency.confidence_score}
                          reasoning={tag.competency.reasoning}
                        />
                      </Box>

                      {/* Chips for status and limiter groups */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={tag.competency.selected ? 'Selected' : 'Not Selected'}
                          size="small"
                          color={tag.competency.selected ? 'primary' : 'default'}
                          variant={tag.competency.selected ? 'filled' : 'outlined'}
                        />
                        {tag.limiter_groups && tag.limiter_groups.length > 0 && (
                          <Chip
                            label={`${tag.limiter_groups.filter(g => g.selected).length}/${tag.limiter_groups.length} Groups`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                        {focusedTagIndex === originalIndex && (
                          <Chip
                            label="Focused"
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Tag Editor Tab */}
        {selectedTab === 2 && (
          <TagEditor
            tag={focusedTag}
            tagIndex={focusedTagIndex}
            lessonId={lessonId}
            onUpdate={onTagUpdate}
          />
        )}
      </Box>
    </Box>
  );
}